import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Link,
  CircularProgress,
  Button
} from '@mui/material';
import { format } from 'date-fns';

const FundProviderTable = ({ data, loading, onRefresh }) => {
  const [ethToUsdRate, setEthToUsdRate] = useState(3500); // Default rate

  // Fetch ETH to USD conversion rate
  useEffect(() => {
    const fetchEthRate = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
        const result = await response.json();
        if (result.ethereum?.usd) {
          setEthToUsdRate(result.ethereum.usd);
        }
      } catch (error) {
        console.error('Error fetching ETH rate:', error);
      }
    };

    fetchEthRate();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy, HH:mm:ss \'ET\' (UTC-04:00)');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatTotalValue = (weiValue) => {
    if (!weiValue || weiValue === 0) return { eth: '0.0000', usd: '0' };
    
    // Convert from wei to ETH (divide by 10^18)
    const eth = typeof weiValue === 'string' 
      ? parseFloat(weiValue) / 1e18 
      : Number(weiValue) / 1e18;
    
    const usd = eth * ethToUsdRate;
    
    return {
      eth: eth.toFixed(4),
      usd: usd.toFixed(2)
    };
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRegisteredColor = (registered) => {
    return registered ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        mt: 2,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflowX: 'auto'
      }}
    >
      <Table sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '180px',
              padding: '16px 12px'
            }}>
              Last Transaction
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '150px',
              padding: '16px 12px'
            }}>
              Name
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '100px',
              padding: '16px 12px'
            }}>
              Code
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '200px',
              padding: '16px 12px'
            }}>
              Address
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '120px',
              padding: '16px 12px'
            }}>
              Registered
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '100px',
              padding: '16px 12px'
            }}>
              Status
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '120px',
              padding: '16px 12px'
            }}>
              Permissions
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '140px',
              padding: '16px 12px'
            }}>
              Transactions
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              minWidth: '180px',
              padding: '16px 12px'
            }}>
              Total Value
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((provider, index) => (
              <TableRow 
                key={index} 
                hover
                sx={{ 
                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                  '&:hover': { backgroundColor: '#f0f8ff' }
                }}
              >
                <TableCell sx={{ padding: '12px' }}>
                  <Typography variant="body2" fontSize="13px">
                    {provider.lastTransactionDate ? formatDate(provider.lastTransactionDate) : 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Typography variant="body2" fontWeight="500" fontSize="13px">
                    {provider.institutionName || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Typography variant="body2" fontWeight="500" fontSize="13px">
                    {provider.institutionCode || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Typography variant="body2" fontSize="13px" sx={{ fontFamily: 'monospace' }}>
                    {provider.walletAddress && provider.walletAddress.length > 0 
                      ? provider.walletAddress[0] 
                      : 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Chip 
                    label={provider.isVerified ? 'Yes' : 'No'} 
                    color={getRegisteredColor(provider.isVerified)}
                    size="small"
                    sx={{ fontSize: '11px', height: '24px' }}
                  />
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Chip 
                    label={provider.isApproved ? 'Active' : 'Pending'} 
                    color={getStatusColor(provider.isApproved ? 'active' : 'pending')}
                    size="small"
                    sx={{ fontSize: '11px', height: '24px' }}
                  />
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Link 
                    href={`/admin-panel/CheckProviderDetail/PermissionsDetail/${provider._id}`}
                    variant="body2"
                    sx={{ fontSize: '13px', textDecoration: 'none', color: '#1976d2' }}
                  >
                    View Detail
                  </Link>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Typography variant="body2" fontWeight="600" fontSize="13px">
                    {provider.transactions || 0}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  {(() => {
                    const totalValue = formatTotalValue(provider.totalValue);
                    return (
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography 
                          variant="body2" 
                          fontWeight="600" 
                          fontSize="13px" 
                          sx={{ 
                            lineHeight: 1.2,
                            color: '#333',
                            marginBottom: '2px'
                          }}
                        >
                          {totalValue.eth} ETH
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="500" 
                          fontSize="13px" 
                          sx={{ 
                            lineHeight: 1.2,
                            color: '#666'
                          }}
                        >
                          ${totalValue.usd} USD
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ padding: '40px' }}>
                <Typography variant="body1" color="text.secondary" fontSize="16px">
                  No fund providers found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FundProviderTable;
